import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  getRichProduct,
  createRichProduct,
  updateRichProduct,
  publishProduct,
  draftProduct,
  scheduleProduct,
  listShippingMethods,
  type ShippingMethod,
  type RichProduct,
  type AdditionalSection,
} from "@/lib/products";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  ArrowLeft,
  Save,
  Zap,
  FileText,
  Clock,
  X,
  Plus,
  Trash2,
  Image,
  Calendar,
  Layers,
  Tag,
  Star,
  FolderOpen,
  ExternalLink,
  Globe,
  DollarSign,
} from "lucide-react";
import { MediaManager } from "@/components/MediaManager";
import { getGangSheetSizes } from "@/hooks/useGangSheetBuilderScript";
import type { GangSheetSize } from "@/lib/types";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical } from "lucide-react";

export const Route = createFileRoute("/app/staff/products/$productId")({
  component: ProductEditor,
});

type Status = "draft" | "scheduled" | "live";
const STATUS_CLS: Record<Status, string> = {
  draft: "bg-muted text-muted-foreground",
  scheduled: "bg-amber-100 text-amber-700",
  live: "bg-emerald-100 text-emerald-700",
};

function Section({
  title,
  icon,
  children,
}: {
  title: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-3xl border border-border bg-card p-6 shadow-soft space-y-4">
      <h2 className="text-base font-semibold flex items-center gap-2">
        {icon}
        {title}
      </h2>
      {children}
    </div>
  );
}

function ChipInput({
  label,
  chips,
  onAdd,
  onRemove,
}: {
  label: string;
  chips: string[];
  onAdd: (v: string) => void;
  onRemove: (v: string) => void;
}) {
  const [val, setVal] = useState("");
  return (
    <div>
      <Label>{label}</Label>
      <div className="flex gap-2 mt-1.5">
        <Input
          value={val}
          onChange={(e) => setVal(e.target.value)}
          placeholder="Type and press Enter"
          className="rounded-2xl"
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              if (val.trim()) {
                onAdd(val.trim());
                setVal("");
              }
            }
          }}
        />
        <Button
          variant="outline"
          size="sm"
          className="rounded-full shrink-0"
          onClick={() => {
            if (val.trim()) {
              onAdd(val.trim());
              setVal("");
            }
          }}
        >
          Add
        </Button>
      </div>
      {chips.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {chips.map((c) => (
            <span
              key={c}
              className="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium"
            >
              {c}
              <button onClick={() => onRemove(c)} className="hover:text-destructive">
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function ProductEditor() {
  const { productId } = Route.useParams();
  const isNew = productId === "new";
  const nav = useNavigate();
  const qc = useQueryClient();

  const { data: existing, isLoading } = useQuery({
    queryKey: ["rich-product", productId],
    queryFn: () => getRichProduct(productId),
    enabled: !isNew,
  });

  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [shortDesc, setShortDesc] = useState("");
  const [fullDesc, setFullDesc] = useState("");
  const [productType, setProductType] = useState("");
  const [imageMainUrl, setImageMainUrl] = useState("");
  const [galleryUrls, setGalleryUrls] = useState<string[]>([]);
  const [galleryInput, setGalleryInput] = useState("");
  const [tags, setTags] = useState<string[]>([]);
  const [collections, setCollections] = useState<string[]>([]);
  const [ribbonEnabled, setRibbonEnabled] = useState(false);
  const [ribbonText, setRibbonText] = useState("");
  const [sections, setSections] = useState<AdditionalSection[]>([]);
  const [variants, setVariants] = useState<
    Array<{ label: string; width_mm?: number; height_mm?: number; price_modifier?: number }>
  >([]);
  const [pricePerM, setPricePerM] = useState("0");
  const [isGangsheet, setIsGangsheet] = useState(false);
  const [allowManual, setAllowManual] = useState(false);
  const [shippingGlobal, setShippingGlobal] = useState(true);
  const [shippingOverrides, setShippingOverrides] = useState<
    Record<string, { enabled: boolean; price?: string }>
  >({});
  const [taxUseGlobal, setTaxUseGlobal] = useState(true);
  const [taxRateOverride, setTaxRateOverride] = useState<string>("");
  const [status, setStatus] = useState<Status>("draft");
  const [goLiveAt, setGoLiveAt] = useState("");
  const [sortOrder, setSortOrder] = useState(0);
  const [showSchedule, setShowSchedule] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [mediaTarget, setMediaTarget] = useState<"main" | number | null>(null);
  const [activeTab, setActiveTab] = useState<"content" | "pricing">("content");

  const { data: allSizes } = useQuery<GangSheetSize[]>({
    queryKey: ["gangsheet-sizes-all"],
    queryFn: getGangSheetSizes,
  });
  const matchedSizesForCode = (allSizes ?? []).filter(
    (s) => existing?.code && s.product_code === existing.code,
  );
  const sizesInMm = matchedSizesForCode.map((s) => {
    const toMm = (v: number, u: string) =>
      u === "mm" ? v : u === "cm" ? v * 10 : u === "in" ? v * 25.4 : v;
    return {
      width: Math.round(toMm(s.width, s.unit)),
      height: Math.round(toMm(s.height, s.unit)),
      label: `${s.width}×${s.height} ${s.unit}`,
    };
  });
  const variantSizeIssues = variants.map((v) => {
    if (!isGangsheet) return null;
    if (!v.width_mm || !v.height_mm) return null;
    if (sizesInMm.length === 0) return null;
    const w = Math.round(v.width_mm);
    const h = Math.round(v.height_mm);
    const matches = sizesInMm.some(
      (s) => (s.width === w && s.height === h) || (s.width === h && s.height === w),
    );
    return matches ? null : "no-match";
  });
  const hasVariantIssues = variantSizeIssues.some(Boolean);

  useEffect(() => {
    if (!existing) return;
    setName(existing.name ?? "");
    setCode(existing.code ?? "");
    setShortDesc(existing.short_description ?? "");
    setFullDesc(existing.full_description ?? "");
    setProductType(existing.product_type ?? "");
    setImageMainUrl(existing.image_main_url ?? "");
    setGalleryUrls(existing.image_gallery_urls ?? []);
    setTags(existing.tags ?? []);
    setCollections(existing.collections ?? []);
    setRibbonEnabled(existing.ribbon_enabled);
    setRibbonText(existing.ribbon_text ?? "");
    setSections(existing.additional_sections ?? []);
    setVariants(Array.isArray(existing.variants) ? (existing.variants as any) : []);
    setPricePerM(String(existing.price_per_m ?? "0"));
    setIsGangsheet(existing.is_gangsheet);
    setAllowManual(existing.allow_manual_upload);
    setShippingGlobal(existing.shipping_use_global);
    const cfg = (existing.shipping_config ?? {}) as {
      methods?: Record<string, { enabled?: boolean; price?: number | string }>;
    };
    const m = cfg.methods ?? {};
    const next: Record<string, { enabled: boolean; price?: string }> = {};
    for (const k of Object.keys(m)) {
      next[k] = {
        enabled: !!m[k]?.enabled,
        price: m[k]?.price !== undefined && m[k]?.price !== null ? String(m[k]?.price) : undefined,
      };
    }
    setShippingOverrides(next);
    setTaxUseGlobal(existing.tax_use_global ?? true);
    setTaxRateOverride(
      existing.tax_rate_override !== undefined && existing.tax_rate_override !== null
        ? String(existing.tax_rate_override)
        : "",
    );
    setStatus(existing.status);
    setGoLiveAt(existing.go_live_at ? existing.go_live_at.slice(0, 16) : "");
    setSortOrder(existing.sort_order ?? 0);
    setIsDirty(false);
  }, [existing]);

  const d = () => setIsDirty(true);

  const buildPayload = (): Partial<Omit<RichProduct, "id" | "created_at">> => ({
    name,
    code: code || undefined,
    description: shortDesc,
    short_description: shortDesc,
    full_description: fullDesc,
    product_type: productType || null,
    image_main_url: imageMainUrl || null,
    image_gallery_urls: galleryUrls,
    tags,
    collections,
    ribbon_enabled: ribbonEnabled,
    ribbon_text: ribbonText || null,
    additional_sections: sections,
    variants,
    price_per_m: pricePerM,
    is_gangsheet: isGangsheet,
    allow_manual_upload: allowManual,
    shipping_use_global: shippingGlobal,
    shipping_config: shippingGlobal
      ? null
      : {
          methods: Object.fromEntries(
            Object.entries(shippingOverrides).map(([id, v]) => [
              id,
              {
                enabled: v.enabled,
                price:
                  v.price !== undefined && v.price !== "" && !Number.isNaN(parseFloat(v.price))
                    ? parseFloat(v.price)
                    : undefined,
              },
            ]),
          ),
        },
    sort_order: sortOrder,
    tax_use_global: taxUseGlobal,
    tax_rate_override: taxUseGlobal
      ? null
      : taxRateOverride !== "" && !Number.isNaN(parseFloat(taxRateOverride))
        ? parseFloat(taxRateOverride)
        : null,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["rich-products"] });
    qc.invalidateQueries({ queryKey: ["rich-product", productId] });
  };

  const save = async (overrides: Partial<RichProduct>) => {
    setSaving(true);
    try {
      if (isNew) {
        const created = await createRichProduct({ ...buildPayload(), ...overrides });
        toast.success("Product saved");
        invalidate();
        nav({ to: "/app/staff/products/$productId", params: { productId: created.id } });
      } else {
        await updateRichProduct(productId, { ...buildPayload(), ...overrides });
        toast.success("Saved");
        invalidate();
        setIsDirty(false);
        if (overrides.status) setStatus(overrides.status as Status);
      }
    } catch (e) {
      toast.error((e as Error).message ?? "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const slug =
    existing?.slug ??
    name
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9-]/g, "");

  if (isLoading) return <div className="h-64 rounded-3xl bg-muted animate-pulse" />;

  return (
    <div className="pb-16 space-y-5">
      {/* top bar */}
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <Link
          to="/app/staff/products"
          className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" /> Products
        </Link>
        <div className="flex items-center gap-2 flex-wrap">
          <span
            className={cn(
              "inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium",
              STATUS_CLS[status],
            )}
          >
            {status === "draft" && <FileText className="h-3 w-3" />}
            {status === "scheduled" && <Clock className="h-3 w-3" />}
            {status === "live" && <Zap className="h-3 w-3" />}
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </span>
          {isDirty && <span className="text-xs text-amber-600 font-medium">Unsaved</span>}
          <Button
            size="sm"
            variant="outline"
            className="rounded-full h-8"
            disabled={saving}
            onClick={() => save({ status: "draft", active: false })}
          >
            <Save className="h-3.5 w-3.5 mr-1" /> Draft
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="rounded-full h-8"
            disabled={saving}
            onClick={() => setShowSchedule((p) => !p)}
          >
            <Calendar className="h-3.5 w-3.5 mr-1" /> Schedule
          </Button>
          <Button
            size="sm"
            className="rounded-full h-8 bg-gradient-hero text-primary-foreground shadow-soft hover:opacity-90"
            disabled={saving}
            onClick={() => save({ status: "live", active: true })}
          >
            <Zap className="h-3.5 w-3.5 mr-1" />
            {saving ? "Saving…" : "Publish"}
          </Button>
        </div>
      </div>

      {showSchedule && (
        <div className="flex flex-wrap items-end gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4">
          <div>
            <Label className="text-xs">Go-live date &amp; time</Label>
            <Input
              type="datetime-local"
              value={goLiveAt}
              onChange={(e) => {
                setGoLiveAt(e.target.value);
                d();
              }}
              className="mt-1 rounded-xl h-8 text-sm"
            />
          </div>
          <Button
            size="sm"
            className="rounded-full h-8"
            disabled={saving || !goLiveAt}
            onClick={() =>
              save({
                status: "scheduled",
                go_live_at: new Date(goLiveAt).toISOString(),
                active: false,
              })
            }
          >
            Confirm schedule
          </Button>
        </div>
      )}

      {/* Gradient header */}
      <div className="relative overflow-hidden rounded-[2rem] bg-gradient-hero p-8 text-primary-foreground shadow-pop">
        <div
          aria-hidden
          className="pointer-events-none absolute -right-16 -top-16 h-64 w-64 rounded-full bg-white/10 blur-2xl"
        />
        <input
          className="w-full bg-transparent text-4xl font-bold placeholder:text-primary-foreground/40 outline-none border-b border-white/20 pb-1 mb-3"
          placeholder="Product name"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            d();
          }}
        />
        <input
          className="w-full bg-transparent text-lg placeholder:text-primary-foreground/40 outline-none text-primary-foreground/90"
          placeholder="Short description"
          value={shortDesc}
          onChange={(e) => {
            setShortDesc(e.target.value);
            d();
          }}
        />
      </div>

      {/* Media manager portal */}
      {mediaTarget !== null && (
        <MediaManager
          onClose={() => setMediaTarget(null)}
          onSelect={(url) => {
            if (mediaTarget === "main") {
              setImageMainUrl(url);
            } else if (typeof mediaTarget === "number" && mediaTarget >= 0) {
              setGalleryUrls((p) => {
                const next = [...p];
                next[mediaTarget] = url;
                return next;
              });
            } else {
              setGalleryUrls((p) => [...p, url]);
            }
            d();
            setMediaTarget(null);
          }}
        />
      )}

      {/* Two-column layout */}
      <div className="grid gap-6 lg:grid-cols-[1fr_272px] items-start">
        <div className="space-y-4">
          <div className="flex gap-1 rounded-2xl bg-muted p-1">
            {(["content", "pricing"] as const).map((t) => (
              <button
                key={t}
                onClick={() => setActiveTab(t)}
                className={cn(
                  "flex-1 rounded-xl py-2 text-sm font-medium transition-all",
                  activeTab === t
                    ? "bg-card shadow-soft text-foreground"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                {t === "content" ? "Content" : "Pricing & Shipping"}
              </button>
            ))}
          </div>
          {activeTab === "content" && (
            <div className="space-y-4">
              <Section title="Images" icon={<Image className="h-4 w-4" />}>
                <div>
                  <Label>Main image</Label>
                  <div className="mt-2 flex items-center gap-3">
                    {imageMainUrl ? (
                      <div className="relative group">
                        <img
                          src={imageMainUrl}
                          alt=""
                          className="h-24 w-24 rounded-2xl object-cover border border-border"
                        />
                        <button
                          onClick={() => {
                            setImageMainUrl("");
                            d();
                          }}
                          className="absolute -right-1.5 -top-1.5 rounded-full bg-destructive p-0.5 text-white opacity-0 group-hover:opacity-100"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex h-24 w-24 items-center justify-center rounded-2xl border-2 border-dashed border-border bg-muted/30">
                        <Image className="h-6 w-6 text-muted-foreground/40" />
                      </div>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-full gap-1.5"
                      onClick={() => setMediaTarget("main")}
                    >
                      <FolderOpen className="h-3.5 w-3.5" />
                      {imageMainUrl ? "Change" : "Choose image"}
                    </Button>
                  </div>
                </div>
                <div>
                  <Label>Gallery</Label>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {galleryUrls.map((url, i) => (
                      <div key={i} className="relative group">
                        <img
                          src={url}
                          alt=""
                          className="h-20 w-20 rounded-xl object-cover border border-border cursor-pointer"
                          onClick={() => setMediaTarget(i)}
                        />
                        <button
                          onClick={() => {
                            setGalleryUrls((p) => p.filter((_, j) => j !== i));
                            d();
                          }}
                          className="absolute -top-1.5 -right-1.5 rounded-full bg-destructive p-0.5 text-white opacity-0 group-hover:opacity-100"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                    <button
                      onClick={() => setMediaTarget(-1)}
                      className="flex h-20 w-20 items-center justify-center rounded-xl border-2 border-dashed border-border hover:border-primary/50 hover:bg-primary/5"
                    >
                      <Plus className="h-5 w-5 text-muted-foreground" />
                    </button>
                  </div>
                </div>
              </Section>
              <Section title="Description & sections" icon={<FileText className="h-4 w-4" />}>
                <Label>Full description</Label>
                <textarea
                  className="mt-1.5 min-h-[120px] w-full rounded-2xl border border-border bg-background px-3 py-2 text-sm"
                  placeholder="Detailed product description…"
                  value={fullDesc}
                  onChange={(e) => {
                    setFullDesc(e.target.value);
                    d();
                  }}
                />
                <div className="border-t border-border pt-4">
                  <p className="mb-2 text-sm font-semibold">Additional sections</p>
                  <p className="mb-3 text-xs text-muted-foreground">
                    These appear as tabs on the customer product page.
                  </p>
                  {sections.map((sec, i) => (
                    <div
                      key={i}
                      className="mb-3 rounded-2xl border border-border bg-muted/20 p-4 space-y-2"
                    >
                      <div className="flex items-center gap-2">
                        <Input
                          className="rounded-xl flex-1"
                          placeholder="Section title"
                          value={sec.title}
                          onChange={(e) => {
                            const u = [...sections];
                            u[i] = { ...u[i], title: e.target.value };
                            setSections(u);
                            d();
                          }}
                        />
                        <button
                          onClick={() => {
                            setSections((p) => p.filter((_, j) => j !== i));
                            d();
                          }}
                          className="text-muted-foreground hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                      <textarea
                        className="min-h-[80px] w-full rounded-xl border border-border bg-background px-3 py-2 text-sm"
                        placeholder="Section content…"
                        value={sec.description}
                        onChange={(e) => {
                          const u = [...sections];
                          u[i] = { ...u[i], description: e.target.value };
                          setSections(u);
                          d();
                        }}
                      />
                    </div>
                  ))}
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-full gap-1"
                    onClick={() => {
                      setSections((p) => [...p, { title: "", description: "" }]);
                      d();
                    }}
                  >
                    <Plus className="h-3.5 w-3.5" />
                    Add section
                  </Button>
                </div>
              </Section>
              <Section title="Options & variants" icon={<Layers className="h-4 w-4" />}>
                <p className="text-xs text-muted-foreground">
                  Options shown to customers as selectable buttons on the product page (e.g. sizes,
                  colours, finishes).
                </p>
                {isGangsheet && hasVariantIssues && (
                  <div className="rounded-2xl border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900 flex items-start gap-2">
                    <span aria-hidden>⚠️</span>
                    <div>
                      <p className="font-semibold">
                        Some variants don't match a configured gang sheet size.
                      </p>
                      <p className="mt-0.5">
                        Customers selecting these options will fall back to all sizes for product
                        code <span className="font-mono">{existing?.code}</span>. Add matching
                        entries in Settings → Products & Pricing → Gang sheet sizes, or fix the
                        variant dimensions.
                      </p>
                    </div>
                  </div>
                )}
                {variants.length === 0 && (
                  <p className="text-xs text-muted-foreground italic">
                    No options yet. Customers will see a single default option.
                  </p>
                )}
                {variants.map((v, i) => (
                  <div
                    key={i}
                    className="rounded-2xl border border-border bg-muted/20 p-3 space-y-2"
                  >
                    <div className="flex items-center gap-2">
                      <Input
                        className="rounded-xl flex-1"
                        placeholder="Option label (e.g. Small, Red, 22×60)"
                        value={v.label}
                        onChange={(e) => {
                          const u = [...variants];
                          u[i] = { ...u[i], label: e.target.value };
                          setVariants(u);
                          d();
                        }}
                      />
                      <button
                        onClick={() => {
                          setVariants((p) => p.filter((_, j) => j !== i));
                          d();
                        }}
                        className="text-muted-foreground hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <Label className="text-[11px]">Width (mm)</Label>
                        <Input
                          type="number"
                          min="0"
                          className="rounded-xl mt-1 h-8 text-sm"
                          value={v.width_mm ?? ""}
                          onChange={(e) => {
                            const u = [...variants];
                            u[i] = {
                              ...u[i],
                              width_mm: e.target.value ? parseFloat(e.target.value) : undefined,
                            };
                            setVariants(u);
                            d();
                          }}
                        />
                      </div>
                      <div>
                        <Label className="text-[11px]">Height (mm)</Label>
                        <Input
                          type="number"
                          min="0"
                          className="rounded-xl mt-1 h-8 text-sm"
                          value={v.height_mm ?? ""}
                          onChange={(e) => {
                            const u = [...variants];
                            u[i] = {
                              ...u[i],
                              height_mm: e.target.value ? parseFloat(e.target.value) : undefined,
                            };
                            setVariants(u);
                            d();
                          }}
                        />
                      </div>
                      <div>
                        <Label className="text-[11px]">Price ± (£)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          className="rounded-xl mt-1 h-8 text-sm"
                          value={v.price_modifier ?? ""}
                          onChange={(e) => {
                            const u = [...variants];
                            u[i] = {
                              ...u[i],
                              price_modifier: e.target.value
                                ? parseFloat(e.target.value)
                                : undefined,
                            };
                            setVariants(u);
                            d();
                          }}
                        />
                      </div>
                    </div>
                    {variantSizeIssues[i] === "no-match" && (
                      <p className="text-[11px] text-amber-700 flex items-center gap-1">
                        <span aria-hidden>⚠️</span>
                        {v.width_mm}×{v.height_mm}mm doesn't match any gang sheet size for{" "}
                        <span className="font-mono">{existing?.code}</span>.
                        {sizesInMm.length > 0 && (
                          <>
                            {" "}
                            Available: {sizesInMm.map((s) => `${s.width}×${s.height}mm`).join(", ")}
                            .
                          </>
                        )}
                      </p>
                    )}
                  </div>
                ))}
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-full gap-1"
                  onClick={() => {
                    setVariants((p) => [...p, { label: "" }]);
                    d();
                  }}
                >
                  <Plus className="h-3.5 w-3.5" /> Add option
                </Button>
                {isGangsheet && (
                  <div className="border-t border-border pt-4 mt-2 space-y-3">
                    <div>
                      <Label className="text-sm font-semibold">Product code</Label>
                      <p className="text-xs text-muted-foreground mb-1.5">
                        Used to group gang sheet sizes for the Drip builder. Pick an existing code
                        to share sizes, or type a new one.
                      </p>
                      <Input
                        list="gangsheet-product-codes"
                        className="rounded-2xl font-mono"
                        placeholder="e.g. dtf-gang"
                        value={code}
                        onChange={(e) => {
                          setCode(e.target.value);
                          d();
                        }}
                      />
                      <datalist id="gangsheet-product-codes">
                        {Array.from(new Set((allSizes ?? []).map((s) => s.product_code))).map(
                          (c) => (
                            <option key={c} value={c} />
                          ),
                        )}
                      </datalist>
                    </div>
                    {code ? (
                      <div>
                        <p className="text-sm font-semibold mb-2">
                          Gang sheet sizes for product code{" "}
                          <span className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded">
                            {code}
                          </span>
                        </p>
                        <GangSheetSizesForCode code={code} />
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground rounded-xl bg-muted p-3">
                        Enter a product code above to start adding gang sheet sizes.
                      </p>
                    )}
                  </div>
                )}
              </Section>
            </div>
          )}
          {activeTab === "pricing" && (
            <Section title="Pricing & Shipping" icon={<DollarSign className="h-4 w-4" />}>
              <div className="grid gap-4 sm:grid-cols-3">
                <div>
                  <Label>Price per metre (£)</Label>
                  <Input
                    className="mt-1.5 rounded-2xl"
                    type="number"
                    min="0"
                    step="0.01"
                    value={pricePerM}
                    onChange={(e) => {
                      setPricePerM(e.target.value);
                      d();
                    }}
                  />
                </div>
                <div>
                  <Label>Product type</Label>
                  <Input
                    className="mt-1.5 rounded-2xl"
                    placeholder="e.g. DTF Gang Sheet"
                    value={productType}
                    onChange={(e) => {
                      setProductType(e.target.value);
                      d();
                    }}
                  />
                </div>
                <div>
                  <Label>Sort order</Label>
                  <Input
                    className="mt-1.5 rounded-2xl"
                    type="number"
                    min="0"
                    value={sortOrder}
                    onChange={(e) => {
                      setSortOrder(parseInt(e.target.value) || 0);
                      d();
                    }}
                  />
                </div>
              </div>
              <div className="border-t border-border pt-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Use global shipping settings</Label>
                    <p className="text-xs text-muted-foreground">
                      Uncheck to configure per-product shipping.
                    </p>
                  </div>
                  <Switch
                    checked={shippingGlobal}
                    onCheckedChange={(v) => {
                      setShippingGlobal(v);
                      d();
                    }}
                  />
                </div>
                {!shippingGlobal && (
                  <CustomShippingPicker
                    overrides={shippingOverrides}
                    onChange={(next: Record<string, { enabled: boolean; price?: string }>) => {
                      setShippingOverrides(next);
                      d();
                    }}
                  />
                )}
              </div>
              <div className="border-t border-border pt-4 space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <Label>Use global tax / VAT</Label>
                    <p className="text-xs text-muted-foreground">
                      Uncheck to set a custom tax rate for this product.
                    </p>
                  </div>
                  <Switch
                    checked={taxUseGlobal}
                    onCheckedChange={(v) => {
                      setTaxUseGlobal(v);
                      d();
                    }}
                  />
                </div>
                {!taxUseGlobal && (
                  <div className="max-w-xs">
                    <Label>Custom tax rate (%)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      className="mt-1.5 rounded-2xl"
                      placeholder="e.g. 0 for zero-rated, 5 for reduced"
                      value={taxRateOverride}
                      onChange={(e) => {
                        setTaxRateOverride(e.target.value);
                        d();
                      }}
                    />
                  </div>
                )}
              </div>
            </Section>
          )}
        </div>
        <div className="space-y-4 lg:sticky lg:top-6">
          {/* Single consolidated sidebar card */}
          <div className="rounded-3xl border border-border bg-card shadow-soft overflow-hidden">
            {/* Preview header */}
            {(imageMainUrl || ribbonEnabled) && (
              <div className="border-b border-border p-4 space-y-2.5 bg-gradient-soft/50">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Preview
                </p>
                <div className="relative rounded-2xl border border-border overflow-hidden bg-card">
                  {imageMainUrl ? (
                    <img src={imageMainUrl} alt="" className="w-full aspect-[4/3] object-cover" />
                  ) : (
                    <div className="w-full aspect-[4/3] bg-muted flex items-center justify-center">
                      <Image className="h-8 w-8 text-muted-foreground/30" />
                    </div>
                  )}
                  {ribbonEnabled && ribbonText && (
                    <span className="absolute right-3 top-3 rounded-full bg-gradient-hero px-2.5 py-0.5 text-xs font-semibold text-primary-foreground shadow-soft">
                      {ribbonText}
                    </span>
                  )}
                </div>
                <p className="text-sm font-medium truncate">{name || "Untitled product"}</p>
              </div>
            )}

            {/* Status & links */}
            <div className="p-5 space-y-3 border-b border-border">
              <h3 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                <Globe className="h-3 w-3" /> Status
              </h3>
              {!isNew && (
                <div className="space-y-1 text-xs text-muted-foreground">
                  <p>
                    <span className="font-medium text-foreground">ID:</span>{" "}
                    {existing?.id?.slice(0, 8)}…
                  </p>
                  <p>
                    <span className="font-medium text-foreground">Slug:</span> /{slug || "—"}
                  </p>
                </div>
              )}
              {!isNew && (
                <a
                  href={`/app/customer/shop/${existing?.id}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs font-medium hover:bg-muted"
                >
                  <ExternalLink className="h-3 w-3" /> View in shop
                </a>
              )}
            </div>

            {/* Tags */}
            <div className="p-5 space-y-2.5 border-b border-border">
              <h3 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                <Tag className="h-3 w-3" /> Tags
              </h3>
              <ChipInput
                label=""
                chips={tags}
                onAdd={(v) => {
                  setTags((p) => (p.includes(v) ? p : [...p, v]));
                  d();
                }}
                onRemove={(v) => {
                  setTags((p) => p.filter((x) => x !== v));
                  d();
                }}
              />
            </div>

            {/* Collections */}
            <div className="p-5 space-y-2.5 border-b border-border">
              <h3 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                <Layers className="h-3 w-3" /> Collections
              </h3>
              <ChipInput
                label=""
                chips={collections}
                onAdd={(v) => {
                  setCollections((p) => (p.includes(v) ? p : [...p, v]));
                  d();
                }}
                onRemove={(v) => {
                  setCollections((p) => p.filter((x) => x !== v));
                  d();
                }}
              />
            </div>

            {/* Gang sheet */}
            <div className="p-5 space-y-3 border-b border-border">
              <h3 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                <Layers className="h-3 w-3" /> Gang sheet & artwork
              </h3>
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <Label className="text-xs">Gang sheet product</Label>
                  <p className="text-[11px] text-muted-foreground">Enables the Drip builder.</p>
                </div>
                <Switch
                  checked={isGangsheet}
                  onCheckedChange={(v) => {
                    setIsGangsheet(v);
                    d();
                  }}
                />
              </div>
              {isGangsheet && (
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <Label className="text-xs">Allow manual upload</Label>
                    <p className="text-[11px] text-muted-foreground">
                      Customers upload a finished gang sheet.
                    </p>
                  </div>
                  <Switch
                    checked={allowManual}
                    onCheckedChange={(v) => {
                      setAllowManual(v);
                      d();
                    }}
                  />
                </div>
              )}
            </div>

            {/* Ribbon */}
            <div className="p-5 space-y-3">
              <h3 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                <Star className="h-3 w-3" /> Ribbon badge
              </h3>
              <div className="flex items-center justify-between gap-3">
                <Label className="text-xs">Show ribbon</Label>
                <Switch
                  checked={ribbonEnabled}
                  onCheckedChange={(v) => {
                    setRibbonEnabled(v);
                    d();
                  }}
                />
              </div>
              {ribbonEnabled && (
                <Input
                  className="rounded-2xl text-xs"
                  placeholder="e.g. New, Featured"
                  value={ribbonText}
                  onChange={(e) => {
                    setRibbonText(e.target.value);
                    d();
                  }}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

type ShippingOverrideMap = Record<string, { enabled: boolean; price?: string }>;

function GangSheetSizesForCode({ code }: { code: string }) {
  const qc = useQueryClient();
  const { data: sizes } = useQuery<GangSheetSize[]>({
    queryKey: ["gangsheet-sizes-all-incl-inactive", code],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("gangsheet_sizes")
        .select("*")
        .eq("product_code", code)
        .order("sort_order", { ascending: true })
        .order("drip_size_id", { ascending: true });
      if (error) throw error;
      return (data || []) as GangSheetSize[];
    },
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["gangsheet-sizes-all-incl-inactive", code] });
    qc.invalidateQueries({ queryKey: ["gangsheet-sizes-all"] });
  };

  const reorderMut = useMutation({
    mutationFn: async (ordered: GangSheetSize[]) => {
      // Persist new sort_order one by one (RLS staff-only)
      for (let i = 0; i < ordered.length; i++) {
        const { error } = await supabase
          .from("gangsheet_sizes")
          .update({ sort_order: i + 1 })
          .eq("id", ordered[i].id);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      invalidate();
      toast.success("Order saved");
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : "Reorder failed"),
  });

  const updateMut = useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: Partial<GangSheetSize> }) => {
      const { error } = await supabase.from("gangsheet_sizes").update(patch).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      invalidate();
      toast.success("Size updated");
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : "Update failed"),
  });

  const deleteMut = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("gangsheet_sizes").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      invalidate();
      toast.success("Size removed");
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : "Delete failed"),
  });

  const createMut = useMutation({
    mutationFn: async (row: Omit<GangSheetSize, "id" | "created_at">) => {
      const { error } = await supabase.from("gangsheet_sizes").insert(row);
      if (error) throw error;
    },
    onSuccess: () => {
      invalidate();
      toast.success("Size added");
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : "Add failed"),
  });

  const [draft, setDraft] = useState({
    drip_size_id: "",
    width: "",
    height: "",
    unit: "in",
    metres_equivalent: "",
  });

  const list = sizes ?? [];
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  const handleDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIndex = list.findIndex((s) => s.id === active.id);
    const newIndex = list.findIndex((s) => s.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const next = arrayMove(list, oldIndex, newIndex);
    // Optimistic cache update
    qc.setQueryData<GangSheetSize[]>(["gangsheet-sizes-all-incl-inactive", code], next);
    reorderMut.mutate(next);
  };

  return (
    <div className="space-y-2">
      {list.length === 0 ? (
        <p className="text-xs text-muted-foreground rounded-xl bg-muted p-3">
          No sizes for product code &quot;{code}&quot; yet. Add one below.
        </p>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={list.map((s) => s.id)} strategy={verticalListSortingStrategy}>
            <div className="rounded-2xl border border-border divide-y divide-border overflow-hidden">
              {list.map((s) => (
                <SortableSizeRow
                  key={s.id}
                  size={s}
                  onSave={(patch) => updateMut.mutate({ id: s.id, patch })}
                  onDelete={() => {
                    if (confirm(`Delete ${s.width}×${s.height} ${s.unit}?`)) deleteMut.mutate(s.id);
                  }}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      <div className="rounded-2xl border border-dashed border-border p-3 bg-muted/40">
        <p className="text-xs font-medium mb-2">Add size</p>
        <div className="grid grid-cols-2 sm:grid-cols-6 gap-2">
          <Input
            className="rounded-xl h-9 text-sm"
            placeholder="Drip ID"
            type="number"
            value={draft.drip_size_id}
            onChange={(e) => setDraft({ ...draft, drip_size_id: e.target.value })}
          />
          <Input
            className="rounded-xl h-9 text-sm"
            placeholder="Width"
            type="number"
            step="0.01"
            value={draft.width}
            onChange={(e) => setDraft({ ...draft, width: e.target.value })}
          />
          <Input
            className="rounded-xl h-9 text-sm"
            placeholder="Height"
            type="number"
            step="0.01"
            value={draft.height}
            onChange={(e) => setDraft({ ...draft, height: e.target.value })}
          />
          <select
            className="rounded-xl h-9 text-sm border border-input bg-background px-2"
            value={draft.unit}
            onChange={(e) => setDraft({ ...draft, unit: e.target.value })}
          >
            <option value="in">in</option>
            <option value="cm">cm</option>
            <option value="mm">mm</option>
          </select>
          <Input
            className="rounded-xl h-9 text-sm"
            placeholder="Metres"
            type="number"
            step="0.001"
            value={draft.metres_equivalent}
            onChange={(e) => setDraft({ ...draft, metres_equivalent: e.target.value })}
          />
          <Button
            className="rounded-xl h-9"
            disabled={
              createMut.isPending ||
              !draft.drip_size_id ||
              !draft.width ||
              !draft.height ||
              !draft.metres_equivalent
            }
            onClick={() => {
              createMut.mutate({
                drip_size_id: Number(draft.drip_size_id),
                width: Number(draft.width),
                height: Number(draft.height),
                unit: draft.unit,
                metres_equivalent: Number(draft.metres_equivalent),
                product_code: code,
                active: true,
              } as Omit<GangSheetSize, "id" | "created_at">);
              setDraft({
                drip_size_id: "",
                width: "",
                height: "",
                unit: "in",
                metres_equivalent: "",
              });
            }}
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

function SizeRow({
  size,
  onSave,
  onDelete,
  dragHandleProps,
  dragRef,
  dragStyle,
  isDragging,
}: {
  size: GangSheetSize;
  onSave: (patch: Partial<GangSheetSize>) => void;
  onDelete: () => void;
  dragHandleProps?: React.HTMLAttributes<HTMLButtonElement>;
  dragRef?: (node: HTMLElement | null) => void;
  dragStyle?: React.CSSProperties;
  isDragging?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    drip_size_id: String(size.drip_size_id),
    width: String(size.width),
    height: String(size.height),
    unit: size.unit,
    metres_equivalent: String(size.metres_equivalent),
    active: size.active,
  });

  if (!editing) {
    return (
      <div
        ref={dragRef}
        style={dragStyle}
        className={cn(
          "flex items-center justify-between gap-2 p-2.5 text-sm bg-background",
          isDragging && "opacity-60 shadow-lg",
        )}
      >
        <div className="flex items-center gap-2 min-w-0">
          <button
            type="button"
            aria-label="Drag to reorder"
            className="shrink-0 cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground touch-none"
            {...dragHandleProps}
          >
            <GripVertical className="h-4 w-4" />
          </button>
          <span className="font-medium">
            {size.width}×{size.height} {size.unit}
          </span>
          {!size.active && (
            <span className="text-[10px] uppercase tracking-wide bg-muted px-1.5 py-0.5 rounded">
              inactive
            </span>
          )}
          <span className="text-xs text-muted-foreground truncate">
            ≈ {Number(size.metres_equivalent).toFixed(2)} m · drip id {size.drip_size_id}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            className="h-8 rounded-lg"
            onClick={() => setEditing(true)}
          >
            Edit
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 rounded-lg text-destructive"
            onClick={onDelete}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div ref={dragRef} style={dragStyle} className="p-2.5 bg-background space-y-2">
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        <Input
          className="rounded-xl h-9 text-sm"
          type="number"
          placeholder="Drip ID"
          value={form.drip_size_id}
          onChange={(e) => setForm({ ...form, drip_size_id: e.target.value })}
        />
        <Input
          className="rounded-xl h-9 text-sm"
          type="number"
          step="0.01"
          placeholder="Width"
          value={form.width}
          onChange={(e) => setForm({ ...form, width: e.target.value })}
        />
        <Input
          className="rounded-xl h-9 text-sm"
          type="number"
          step="0.01"
          placeholder="Height"
          value={form.height}
          onChange={(e) => setForm({ ...form, height: e.target.value })}
        />
        <select
          className="rounded-xl h-9 text-sm border border-input bg-background px-2"
          value={form.unit}
          onChange={(e) => setForm({ ...form, unit: e.target.value })}
        >
          <option value="in">in</option>
          <option value="cm">cm</option>
          <option value="mm">mm</option>
        </select>
        <Input
          className="rounded-xl h-9 text-sm"
          type="number"
          step="0.001"
          placeholder="Metres"
          value={form.metres_equivalent}
          onChange={(e) => setForm({ ...form, metres_equivalent: e.target.value })}
        />
      </div>
      <div className="flex items-center justify-between">
        <label className="flex items-center gap-2 text-xs">
          <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
          Active
        </label>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            className="h-8 rounded-lg"
            onClick={() => setEditing(false)}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            className="h-8 rounded-lg"
            onClick={() => {
              onSave({
                drip_size_id: Number(form.drip_size_id),
                width: Number(form.width),
                height: Number(form.height),
                unit: form.unit,
                metres_equivalent: Number(form.metres_equivalent),
                active: form.active,
              });
              setEditing(false);
            }}
          >
            Save
          </Button>
        </div>
      </div>
    </div>
  );
}

function SortableSizeRow({
  size,
  onSave,
  onDelete,
}: {
  size: GangSheetSize;
  onSave: (patch: Partial<GangSheetSize>) => void;
  onDelete: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: size.id,
  });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  return (
    <SizeRow
      size={size}
      onSave={onSave}
      onDelete={onDelete}
      dragRef={setNodeRef}
      dragStyle={style}
      isDragging={isDragging}
      dragHandleProps={{ ...attributes, ...listeners } as React.HTMLAttributes<HTMLButtonElement>}
    />
  );
}

function CustomShippingPicker({
  overrides,
  onChange,
}: {
  overrides: ShippingOverrideMap;
  onChange: (next: ShippingOverrideMap) => void;
}) {
  const { data: methods } = useQuery<ShippingMethod[]>({
    queryKey: ["shipping-methods-all"],
    queryFn: listShippingMethods,
  });

  if (!methods || methods.length === 0) {
    return (
      <p className="text-xs text-muted-foreground rounded-xl bg-muted p-3">
        No shipping methods defined yet. Add some in Settings → Products & Pricing.
      </p>
    );
  }

  const update = (id: string, patch: Partial<{ enabled: boolean; price?: string }>) => {
    const current = overrides[id] ?? { enabled: false };
    onChange({ ...overrides, [id]: { ...current, ...patch } });
  };

  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground">
        Choose which shipping methods customers see for this product. Optionally override the price.
      </p>
      <div className="rounded-2xl border border-border divide-y divide-border overflow-hidden">
        {methods.map((m) => {
          const o = overrides[m.id] ?? { enabled: false };
          return (
            <div key={m.id} className="flex items-center gap-3 p-3 bg-background">
              <Switch checked={o.enabled} onCheckedChange={(v) => update(m.id, { enabled: v })} />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{m.name}</div>
                <div className="text-xs text-muted-foreground truncate">
                  Default £{Number(m.price).toFixed(2)}
                  {m.description ? ` · ${m.description}` : ""}
                </div>
              </div>
              <div className="w-28">
                <Input
                  className="rounded-xl text-sm h-9"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="Override £"
                  value={o.price ?? ""}
                  disabled={!o.enabled}
                  onChange={(e) => update(m.id, { price: e.target.value })}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
